import { satisfies as satisfiesSemver } from 'compare-versions';

import {
  DatabaseComponent,
  DatabaseInstance,
  DboCacheConfig,
  DboCacheGroup,
  DboCacheStatus,
  DboCluster,
  DbProtocolType,
  TtlMode,
} from '@cast/types';
import { toNumOrUndefined, toNumOrZero } from '@cast/utils';

import { optimalCachingDiffFraction } from './_constants';
import {
  CacheMetrics,
  DbInstanceType,
  InstancePrice,
  InstanceRowModel,
} from './_types';
import {
  DBO_AUTODISCOVERY_VERSION,
  DBO_CONNECTION_TRACKING_VERSION,
  DBO_CONNECTION_TRACKING_VERSION_MYSQL,
} from './constants';

/**
 * Takes an array of values that are sorted in descending order
 * and returns an array where each following value is the difference between the current value and the last.
 * All of the resulting values add up to `values[0]`. Used to create progress bars and pie charts.
 **/
export const createOverlappingValues = <
  T extends { value?: number | string | null }
>(
  values: T[]
): (T & { value: number })[] => {
  const filtered = values.filter((item) => toNumOrUndefined(item.value));

  return filtered.reduce((acc, item, index) => {
    const diff =
      toNumOrZero(item.value) - toNumOrZero(filtered.at(index + 1)?.value);
    if (diff > 0) {
      acc.push({
        ...item,
        value: diff,
      });
    }
    return acc;
  }, [] as (T & { value: number })[]);
};

/**
 * Caps a displayed rounded savings percent value to '<1', value or '>99'.
 * Avoids situations where we show values like 0% -> 0% or 99% -> 100% where the projected savings aren't 100%.
 */
export const mapProjectedSavingsPercent = (
  savingsPercent: number,
  roundedSavingsPercent: number
): string | number =>
  savingsPercent < 100 && roundedSavingsPercent === 100
    ? '>99'
    : savingsPercent > 0 && roundedSavingsPercent === 0
    ? '<1'
    : roundedSavingsPercent;

export const isMetricOptimal = (
  current: number,
  projected: number
): boolean => {
  if (current >= projected) {
    return true;
  }
  return current >= projected - projected * optimalCachingDiffFraction;
};

export const isCacheUndeployed = (
  cacheGroup: Pick<DboCacheGroup, 'cacheStatus'>
): boolean => {
  return cacheGroup.cacheStatus === DboCacheStatus.Undeployed;
};

export const isDbAutodiscoverySupported = (
  cacheGroup: Pick<DboCacheGroup, 'helmChartVersion'>
): boolean => {
  return satisfiesSemver(
    cacheGroup.helmChartVersion || '0.0.0',
    `>=${DBO_AUTODISCOVERY_VERSION}`
  );
};

export const isConnectionTrackingSupported = (
  cacheGroup: Pick<DboCacheGroup, 'helmChartVersion' | 'protocolType'>
): boolean => {
  const minVersion =
    cacheGroup.protocolType === DbProtocolType.MySQL
      ? DBO_CONNECTION_TRACKING_VERSION_MYSQL
      : DBO_CONNECTION_TRACKING_VERSION;
  return satisfiesSemver(
    cacheGroup.helmChartVersion || '0.0.0',
    `>=${minVersion}`
  );
};

export const isConnectionPoolingSupported = (
  cacheGroup: Pick<
    DboCacheGroup,
    'helmChartVersion' | 'protocolType' | 'cacheStatus'
  >
): boolean => {
  return (
    isConnectionTrackingSupported(cacheGroup) &&
    !!cacheGroup.cacheStatus &&
    !isCacheUndeployed(cacheGroup)
  );
};

export const isConnectionPoolingEnabled = (
  cacheGroup: Pick<DboCacheGroup, 'statusConditions'>
): boolean => {
  return cacheGroup.statusConditions?.poolingEnabled === true;
};

export const hasOnlyProjectedCacheHits = ({
  cacheHits,
  projectedCacheHits,
}: {
  cacheHits?: string | number;
  projectedCacheHits?: string | number;
} = {}): boolean => {
  return toNumOrZero(cacheHits) === 0 && toNumOrZero(projectedCacheHits) > 0;
};

export const isManualCacheGroup = (cacheGroup: DboCacheGroup): boolean => {
  return !cacheGroup.instanceId && !cacheGroup.clusterId;
};

export const isRoxyCacheGroup = (
  cacheGroup?: Pick<DboCacheGroup, 'roxy'>
): cacheGroup is DboCacheGroup & { roxy: true } => {
  return cacheGroup?.roxy === true;
};

export const getInstanceType = ({
  cluster,
  instance,
}: DatabaseComponent): DbInstanceType => {
  if (cluster || instance?.rds) {
    return DbInstanceType.RDS;
  }
  return DbInstanceType.UNKNOWN;
};

type GetDefaultCacheConfigProps = {
  name: string;
};
export const getDefaultCacheConfig = ({
  name,
}: GetDefaultCacheConfigProps): DboCacheConfig => ({
  databaseName: name,
  mode: TtlMode.DontCache,
});

type GetIsPerformanceInsightsDisabledProps = {
  instance?: DatabaseInstance;
  cluster?: DboCluster;
};
export const getIsPerformanceInsightsDisabled = ({
  instance,
  cluster,
}: GetIsPerformanceInsightsDisabledProps): boolean => {
  if (instance?.rds) {
    return instance.rds.performanceInsightsEnabled === false;
  }
  if (cluster?.instances?.length) {
    return cluster.instances.every((instance) =>
      getIsPerformanceInsightsDisabled({ instance })
    );
  }
  return false;
};

export const getComponentDbCount = (
  component: DatabaseInstance | DboCluster
): {
  total: number;
  cacheEnabled: number;
} => {
  return {
    total: component.databases?.length || 0,
    cacheEnabled:
      component.databases?.filter(({ cacheEnabled }) => cacheEnabled).length ||
      0,
  };
};

export const getCacheGroupDbCount = (
  cacheGroup: DboCacheGroup
): {
  total: number;
  cacheEnabled: number;
} => {
  return {
    total: cacheGroup.configurations?.length || 0,
    cacheEnabled:
      cacheGroup.configurations?.filter((c) => c.mode !== TtlMode.DontCache)
        .length || 0,
  };
};

type GetDbCountProps = {
  cacheGroup?: DboCacheGroup;
  instance?: DatabaseInstance;
  cluster?: DboCluster;
};
export const getDbCount = ({
  cacheGroup,
  instance,
  cluster,
}: GetDbCountProps): {
  total: number | undefined;
  cacheEnabled: number | undefined;
} => {
  // Only get db count from the db-component if a cache group is undeployed.
  // Cache group is the primary source of truth.
  if (cacheGroup && !isCacheUndeployed(cacheGroup)) {
    return getCacheGroupDbCount(cacheGroup);
  }

  const component = instance || cluster;
  if (component) {
    return getComponentDbCount(component);
  }

  return {
    total: undefined,
    cacheEnabled: undefined,
  };
};

type GetCacheMetricsProps = {
  cacheGroup?: Pick<DboCacheGroup, 'metrics'>;
  instance?: Pick<DatabaseInstance, 'metricsOverview'>;
  cluster?: Pick<DboCluster, 'metricsOverview'>;
  preferCacheGroupMetrics?: boolean;
  performanceInsightsDisabled?: boolean;
};
export const getCacheMetrics = ({
  cacheGroup,
  instance,
  cluster,
  preferCacheGroupMetrics,
  performanceInsightsDisabled,
}: GetCacheMetricsProps): CacheMetrics => {
  const metrics: CacheMetrics = {
    totalQueries: undefined,
    dqlQueries: undefined,
    cacheHits: undefined,
    potentialCacheHits: undefined,
    hitRate: undefined,
    potentialHitRate: undefined,
    totalDbTime: undefined,
    timeSavedPercent: undefined,
    potentialTimeSavedPercent: undefined,
    load: undefined,
  };

  const component = instance || cluster;
  if (component && !performanceInsightsDisabled) {
    const { metricsOverview } = component;
    metrics.totalQueries = toNumOrUndefined(metricsOverview?.totalQueries);
    metrics.dqlQueries = toNumOrUndefined(metricsOverview?.totalReadQueries);
    metrics.load = toNumOrUndefined(metricsOverview?.databaseLoad);
  }

  if (cacheGroup && (!component || preferCacheGroupMetrics)) {
    const { metrics: cacheMetrics } = cacheGroup;
    metrics.totalQueries = toNumOrUndefined(cacheMetrics?.totalQueries);
    metrics.dqlQueries = toNumOrUndefined(cacheMetrics?.dqlQueries);
    metrics.hitRate = toNumOrUndefined(cacheMetrics?.cacheHitRate);
    metrics.potentialHitRate = toNumOrUndefined(cacheMetrics?.projectedHitRate);
    metrics.cacheHits = toNumOrUndefined(cacheMetrics?.cacheHits);
    metrics.potentialCacheHits = toNumOrUndefined(
      cacheMetrics?.projectedCacheHits
    );
    metrics.totalDbTime = toNumOrUndefined(cacheMetrics?.totalTime);
    metrics.timeSavedPercent = toNumOrUndefined(cacheMetrics?.timeSavedPercent);
    metrics.potentialTimeSavedPercent = toNumOrUndefined(
      cacheMetrics?.projectedTimeSavedPercent
    );
  }

  return metrics;
};

type GetInstancePriceProps = {
  instance?: DatabaseInstance;
  cluster?: DboCluster;
  performanceInsightsDisabled?: boolean;
};
export const getInstancePrice = ({
  instance,
  cluster,
  performanceInsightsDisabled,
}: GetInstancePriceProps = {}): InstancePrice => {
  const price: InstancePrice = {
    current: undefined,
    savings: undefined,
    savingsPercent: undefined,
  };

  if (performanceInsightsDisabled) {
    return price;
  }

  if (instance?.rds) {
    const { rds } = instance;
    price.current = toNumOrUndefined(rds.currentInstanceDetails.instancePrice);
    price.savings = toNumOrUndefined(rds.potentialSavings);
    const savingsFraction = toNumOrUndefined(rds.potentialSavingsFraction);
    price.savingsPercent = savingsFraction ? savingsFraction * 100 : undefined;
  } else if (cluster) {
    price.current = toNumOrUndefined(cluster.currentCost);
    price.savings = toNumOrUndefined(cluster.potentialSavings);
    const savingsFraction = toNumOrUndefined(cluster.potentialSavingsFraction);
    price.savingsPercent = savingsFraction ? savingsFraction * 100 : undefined;
  }

  return price;
};

const instanceDbTypeToProtocolType = (
  instance: DatabaseInstance
): DbProtocolType => {
  switch (instance.configuration.type) {
    case 'PostgreSQL':
    case 'AuroraPostgreSQL':
      return DbProtocolType.PostgreSQL;
    case 'MySQL':
      return DbProtocolType.MySQL;
    default:
      return DbProtocolType.Unspecified;
  }
};

export const instanceToRowModel = (
  instance: DatabaseInstance,
  cacheGroup?: DboCacheGroup
): InstanceRowModel => {
  const performanceInsightsDisabled = getIsPerformanceInsightsDisabled({
    instance,
  });
  return {
    id: instance.id,
    name: instance.name,
    instanceId: instance.id,
    cacheGroupId: instance.cacheGroupId ?? cacheGroup?.id,
    componentType: 'Instance',
    instanceType: getInstanceType({ instance }),
    instanceFamily: instance.rds?.currentInstanceDetails.instanceType,
    price: getInstancePrice({ instance, performanceInsightsDisabled }),
    metrics: getCacheMetrics({
      cacheGroup,
      instance,
      preferCacheGroupMetrics: !!cacheGroup && !isCacheUndeployed(cacheGroup),
      performanceInsightsDisabled,
    }),
    dbCount: getDbCount({ cacheGroup, instance }),
    dbType: cacheGroup?.protocolType || instanceDbTypeToProtocolType(instance),
    cacheStatus: cacheGroup?.cacheStatus,
    cacheStatusConditions: cacheGroup?.statusConditions,
    directMode: cacheGroup?.directMode ?? false,
    roxy: cacheGroup?.roxy,
    performanceInsightsDisabled,
  };
};

export const clusterToRowModel = (
  cluster: DboCluster,
  cacheGroup?: DboCacheGroup
): InstanceRowModel => {
  const performanceInsightsDisabled = getIsPerformanceInsightsDisabled({
    cluster,
  });
  return {
    id: cluster.id,
    name: cluster.name,
    clusterId: cluster.id,
    cacheGroupId: cluster.cacheGroupId ?? cacheGroup?.id,
    componentType: 'Cluster',
    instanceType: getInstanceType({ cluster }),
    clusterType: cluster.type,
    price: getInstancePrice({ cluster, performanceInsightsDisabled }),
    metrics: getCacheMetrics({
      cacheGroup,
      cluster,
      preferCacheGroupMetrics: !!cacheGroup && !isCacheUndeployed(cacheGroup),
      performanceInsightsDisabled,
    }),
    dbCount: getDbCount({ cacheGroup, cluster }),
    dbType: cacheGroup?.protocolType || DbProtocolType.Unspecified,
    cacheStatus: cacheGroup?.cacheStatus,
    cacheStatusConditions: cacheGroup?.statusConditions,
    directMode: cacheGroup?.directMode ?? false,
    roxy: cacheGroup?.roxy,
    performanceInsightsDisabled,
  };
};

export const cacheGroupToRowModel = (
  cacheGroup: DboCacheGroup
): InstanceRowModel => {
  const metadata: Pick<InstanceRowModel, 'componentType' | 'instanceType'> =
    (() => {
      if (cacheGroup.instanceId) {
        return {
          componentType: 'Instance',
          instanceId: cacheGroup.instanceId,
          instanceType: DbInstanceType.RDS,
        };
      }
      if (cacheGroup.clusterId) {
        return {
          componentType: 'Cluster',
          clusterId: cacheGroup.clusterId,
          instanceType: DbInstanceType.RDS,
        };
      }
      return {
        componentType: 'Manual Component',
        instanceType: DbInstanceType.MANUAL,
      };
    })();
  return {
    id: cacheGroup.id!,
    name: cacheGroup.name || '',
    cacheGroupId: cacheGroup.id,
    price: getInstancePrice(),
    metrics: getCacheMetrics({ cacheGroup }),
    dbType: cacheGroup.protocolType,
    dbCount: getCacheGroupDbCount(cacheGroup),
    cacheStatus: cacheGroup.cacheStatus,
    cacheStatusConditions: cacheGroup.statusConditions,
    directMode: cacheGroup.directMode ?? false,
    roxy: cacheGroup.roxy,
    ...metadata,
  };
};

export const isHelmOnly = (
  poolingEnabled?: boolean,
  dbType?: DbProtocolType
): boolean => {
  return (dbType === 'MySQL' && !!poolingEnabled) || dbType === 'Oracle';
};
